import React, { useMemo, useState } from 'react'
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Th,
  Tr,
  HStack,
  Input,
  Text,
  useToast
} from '@chakra-ui/react'
import { rentals } from '@homevest/utils'
import {
  useCreateMoveInFeeMutation,
  useUpdateMoveInFeeAmountMutation,
  useDeactivateMoveInFeeMutation,
  useRentRollMoveInFeesDataQuery,
  Upup_LiabilityTypesQuery
} from 'graphql/generated'
import { ContentSectionCard } from 'components/TailwindUIToolkit'
import LiabilityTypesDropdown from './Dropdowns/LiabilityTypesDropdown'
import { formatMoney } from 'lib/numbers'
import { Label } from 'components/Toolkit'

type MoveInFee = {
  id: string
  amount: number
  name: string
}

type LiabilityType = Upup_LiabilityTypesQuery['liability_types'][number]

const AddMoveInFeeModal: React.FC<{
  rentalId: string
  isModalOpen: boolean
  onSuccess?: () => void
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ rentalId, isModalOpen, onSuccess, setIsModalOpen }) => {
  const [selectedLiabilityType, setSelectedLiabilityType] = useState<LiabilityType | undefined>(
    undefined
  )
  const [newFeeAmount, setNewFeeAmount] = useState<number | ''>('')
  const [_, createMoveInFee] = useCreateMoveInFeeMutation()
  const toast = useToast()

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedLiabilityType(undefined)
    setNewFeeAmount('')
  }

  return (
    <Modal isOpen={isModalOpen} onClose={closeModal}>
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>Add a Move-In Fee</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <LiabilityTypesDropdown
              disabled={false}
              onSelect={(lt) => setSelectedLiabilityType(lt)}
              value={selectedLiabilityType}
            />
            <Label>Amount</Label>
            <Input
              type='number'
              value={newFeeAmount ?? ''}
              onChange={(e) => setNewFeeAmount(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              size='xs'
              colorScheme='teal'
              isDisabled={!selectedLiabilityType?.id || !newFeeAmount}
              onClick={async (e) => {
                e.stopPropagation()
                const res = await createMoveInFee({
                  rentalId,
                  liabilityTypeId: selectedLiabilityType!.id,
                  amount: newFeeAmount
                })
                if (res.error) {
                  toast({
                    title: 'Error',
                    description: res.error.message,
                    status: 'error',
                    duration: 5000,
                    position: 'bottom-right',
                    isClosable: true
                  })
                } else {
                  closeModal()
                  onSuccess?.()
                }
              }}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}

const ConfirmDeleteMoveInFeeModal: React.FC<{
  id: string
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
  isConfirmDeleteModalOpen: boolean
  onSuccess?: () => void
  setIsConfirmDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ id, setIsEditing, isConfirmDeleteModalOpen, onSuccess, setIsConfirmDeleteModalOpen }) => {
  const [_, deactivateMoveInFee] = useDeactivateMoveInFeeMutation()
  const toast = useToast()

  return (
    <Modal isOpen={isConfirmDeleteModalOpen} onClose={() => setIsConfirmDeleteModalOpen(false)}>
      <ModalOverlay>
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Really delete this move-in fee?</ModalHeader>
          <Button
            size='xs'
            margin={3}
            colorScheme='red'
            onClick={async () => {
              const res = await deactivateMoveInFee({ id, deactivated_at: new Date() })
              if (res.error) {
                toast({
                  title: 'Error',
                  description: res.error.message,
                  status: 'error',
                  duration: 5000,
                  position: 'bottom-right',
                  isClosable: true
                })
              } else {
                setIsConfirmDeleteModalOpen(false)
                setIsEditing(false)
                onSuccess?.()
              }
            }}
          >
            Delete
          </Button>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}

const MoveInFeeActions: React.FC<{
  id: string
  isEditing: boolean
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
  feeAmount: number | ''
  setFeeAmount: React.Dispatch<React.SetStateAction<number | ''>>
  onSuccess?: () => void
  originalFeeAmount: number
}> = ({ id, isEditing, setIsEditing, feeAmount, setFeeAmount, onSuccess, originalFeeAmount }) => {
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false)
  const [_, updateMoveInFeeAmount] = useUpdateMoveInFeeAmountMutation()
  const toast = useToast()

  if (isEditing) {
    return (
      <HStack>
        <Button
          size='xs'
          colorScheme='teal'
          onClick={async () => {
            const res = await updateMoveInFeeAmount({ id, amount: feeAmount })
            if (res.error) {
              toast({
                title: 'Error',
                description: res.error.message,
                status: 'error',
                duration: 5000,
                position: 'bottom-right',
                isClosable: true
              })
            } else {
              setIsEditing(false)
              onSuccess?.()
            }
          }}
        >
          Submit
        </Button>
        <Button
          size='xs'
          onClick={() => {
            setFeeAmount(originalFeeAmount)
            setIsEditing(false)
          }}
        >
          Cancel
        </Button>
        <Button size='xs' colorScheme='red' onClick={() => setIsConfirmDeleteModalOpen(true)}>
          Delete
        </Button>
        <ConfirmDeleteMoveInFeeModal
          {...{
            id,
            setIsEditing,
            isConfirmDeleteModalOpen,
            onSuccess,
            setIsConfirmDeleteModalOpen
          }}
        />
      </HStack>
    )
  }

  return (
    <Button size='xs' colorScheme='teal' onClick={() => setIsEditing(true)}>
      Edit
    </Button>
  )
}

const MoveInFeeRow: React.FC<MoveInFee & { onSuccess?: () => void }> = ({
  id,
  amount,
  name,
  onSuccess
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [feeAmount, setFeeAmount] = useState<number | ''>(amount)

  return (
    <Tr>
      <Td>{name}</Td>
      <Td>
        {isEditing ? (
          <Input
            type='number'
            min={0}
            value={feeAmount ?? ''}
            onChange={(e) => setFeeAmount(e.target.value === '' ? '' : Number(e.target.value))}
          />
        ) : (
          formatMoney(feeAmount || null, 2, '$')
        )}
      </Td>
      <Td>
        <MoveInFeeActions
          {...{
            id,
            isEditing,
            setIsEditing,
            setFeeAmount,
            feeAmount,
            onSuccess,
            originalFeeAmount: amount
          }}
        />
      </Td>
    </Tr>
  )
}

const MoveInFeesPanel: React.FC<{
  onMonetaryDataChange?: () => void
  rentalId: string
}> = ({ onMonetaryDataChange, rentalId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const queryContext = useMemo(() => ({ suspense: false }), [])
  const [{ data, fetching, error }, refetchMoveInFees] = useRentRollMoveInFeesDataQuery({
    variables: { rentalId },
    requestPolicy: 'cache-and-network',
    context: queryContext
  })
  const rental = data?.rentals_by_pk

  const refreshMoveInFees = () => {
    refetchMoveInFees({ requestPolicy: 'network-only' })
    onMonetaryDataChange?.()
  }

  if (fetching && !rental) {
    return (
      <ContentSectionCard title='Move-In Fees'>
        <Text color='gray.500'>Loading move-in fees...</Text>
      </ContentSectionCard>
    )
  }

  if (error && !rental) {
    return (
      <ContentSectionCard title='Move-In Fees'>
        <Text color='red.600'>Unable to load move-in fees.</Text>
      </ContentSectionCard>
    )
  }

  if (!rental) {
    return null
  }

  // Don't show this panel if rental is no longer pending
  if (rental.status !== rentals.RENTAL_STATUS.PENDING) {
    return null
  }

  if (fetching && !Array.isArray(rental.rental_move_in_fees)) {
    return (
      <ContentSectionCard title='Move-In Fees'>
        <Text color='gray.500'>Loading move-in fees...</Text>
      </ContentSectionCard>
    )
  }

  if (error && !Array.isArray(rental.rental_move_in_fees)) {
    return (
      <ContentSectionCard title='Move-In Fees'>
        <Text color='red.600'>Unable to load move-in fees.</Text>
      </ContentSectionCard>
    )
  }

  if (!Array.isArray(rental.rental_move_in_fees)) {
    return null
  }

  const moveInFees = rental.rental_move_in_fees.map((mif) => ({
    id: mif.id,
    name: mif.liability_type.name,
    amount: mif.amount
  }))

  return (
    <ContentSectionCard
      title='Move-In Fees'
      action={
        <Button
          size='xs'
          colorScheme='teal'
          onClick={(e) => {
            e.stopPropagation()
            setIsModalOpen(true)
          }}
        >
          + Move-In Fee
        </Button>
      }
    >
      {moveInFees.length > 0 && (
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Move-In Fee</Th>
                <Th>Amount</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {moveInFees.map((mif) => (
                <MoveInFeeRow key={mif.id} {...mif} onSuccess={refreshMoveInFees} />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <AddMoveInFeeModal
        {...{
          rentalId: rental.id,
          isModalOpen,
          onSuccess: refreshMoveInFees,
          setIsModalOpen
        }}
      />
    </ContentSectionCard>
  )
}

export default MoveInFeesPanel
